import { useCallback, useEffect, useRef, useState } from "react";
import { IoSearchOutline } from "react-icons/io5";
import { MdMyLocation } from "react-icons/md";
import { RxCross2 } from "react-icons/rx";
import { ClipLoader } from "react-spinners";
import InteractiveLocationMap from "../../../components/InteractiveLocationMap";
import { useMapProvider } from "../../../hooks/useMapProvider";
import { useTranslateText } from "../../../hooks/useTranslateText";
import { updateSendMessageData } from "../../../store/Slices/SendMessageSlice";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import TextTranslate from "../../../utils/TextTranslate";
import {
  fetchNearbyPlaces,
  NearbyPlace,
  reverseGeocode,
  searchPlaces,
} from "./geocode";
import ShareLocation from "./ShareLocation";

export default function SearchLocationDropdown({
  onClose,
}: {
  onClose?: () => void;
}) {
  const dispatch = useAppDispatch();
  const translate = useTranslateText();
  const { provider } = useMapProvider();
  const sendMessageData = useAppSelector((state) => state.SendMessageData);

  const [inputValue, setInputValue] = useState(sendMessageData.message || "");
  const [searchResults, setSearchResults] = useState<NearbyPlace[]>([]);
  const [nearbyPlaces, setNearbyPlaces] = useState<NearbyPlace[]>([]);
  const [loadingGeo, setLoadingGeo] = useState(true);
  const [loadingNearby, setLoadingNearby] = useState(false);
  const [searching, setSearching] = useState(false);
  const [geoError, setGeoError] = useState("");
  const searchTimer = useRef<number | null>(null);
  const nearbyReqId = useRef(0);

  const lat = Number(sendMessageData.latitude);
  const lng = Number(sendMessageData.longitude);
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);

  const applyLocation = useCallback(
    async (nextLat: number, nextLng: number, label?: string) => {
      let message = label;
      if (!message) {
        try {
          message = await reverseGeocode(nextLat, nextLng, provider);
        } catch {
          message = `${nextLat.toFixed(5)}, ${nextLng.toFixed(5)}`;
        }
      }

      setInputValue(message);
      dispatch(
        updateSendMessageData({
          latitude: nextLat,
          longitude: nextLng,
          message,
          message_type: "location",
        }),
      );
    },
    [dispatch, provider],
  );

  const loadNearby = useCallback(
    async (nextLat: number, nextLng: number) => {
      const reqId = ++nearbyReqId.current;
      setLoadingNearby(true);
      try {
        const places = await fetchNearbyPlaces(nextLat, nextLng, provider);
        if (reqId === nearbyReqId.current) {
          setNearbyPlaces(places);
        }
      } catch {
        if (reqId === nearbyReqId.current) {
          setNearbyPlaces([]);
        }
      } finally {
        if (reqId === nearbyReqId.current) {
          setLoadingNearby(false);
        }
      }
    },
    [provider],
  );

  const locateUser = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setGeoError("Geolocation is not supported by your browser.");
      setLoadingGeo(false);
      return;
    }

    setLoadingGeo(true);
    setGeoError("");
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const nextLat = position.coords.latitude;
        const nextLng = position.coords.longitude;
        await applyLocation(nextLat, nextLng);
        await loadNearby(nextLat, nextLng);
        setLoadingGeo(false);
      },
      () => {
        setGeoError("Location access denied. Enable GPS and try again.");
        setLoadingGeo(false);
        // Moscow fallback so the sheet is still usable.
        if (!hasCoords) {
          void applyLocation(55.751244, 37.618423, "Moscow");
          void loadNearby(55.751244, 37.618423);
        }
      },
      { enableHighAccuracy: true, timeout: 12000 },
    );
  }, [applyLocation, hasCoords, loadNearby]);

  useEffect(() => {
    locateUser();
    // Initial GPS locate once when the sheet opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasCoords) return;
    void loadNearby(lat, lng);
  }, [hasCoords, lat, lng, loadNearby]);

  useEffect(() => {
    if (searchTimer.current) {
      window.clearTimeout(searchTimer.current);
    }

    const q = inputValue.trim();
    if (q.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    searchTimer.current = window.setTimeout(async () => {
      try {
        const results = await searchPlaces(
          q,
          provider,
          hasCoords ? { lat, lng } : undefined,
        );
        setSearchResults(results);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);

    return () => {
      if (searchTimer.current) {
        window.clearTimeout(searchTimer.current);
      }
    };
  }, [inputValue, provider, hasCoords, lat, lng]);

  const selectPlace = async (place: NearbyPlace) => {
    setSearchResults([]);
    await applyLocation(place.lat, place.lng, place.label || place.name);
    await loadNearby(place.lat, place.lng);
  };

  const handleMapChange = async (nextLat: number, nextLng: number) => {
    await applyLocation(nextLat, nextLng);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 space-y-3 px-4 pt-3">
        <div className="relative">
          <IoSearchOutline className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lg text-lightText" />
          <input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            className="w-full rounded-[10px] border-0 bg-black/[0.06] py-2.5 pl-10 pr-10 text-[15px] text-darkText outline-none placeholder:text-lightText dark:bg-white/[0.1]"
            type="search"
            enterKeyHint="search"
            placeholder={translate("Add Location")}
          />
          {inputValue.length > 0 && (
            <button
              type="button"
              aria-label="Clear"
              onClick={() => setInputValue("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-lightText"
            >
              <RxCross2 className="text-lg" />
            </button>
          )}
        </div>

        {searchResults.length > 0 && (
          <div className="max-h-40 overflow-y-auto rounded-2xl bg-secondary shadow-sm ring-1 ring-black/[0.04] dark:ring-white/[0.06]">
            <div className="[&>*+*]:border-t [&>*+*]:border-black/[0.06] dark:[&>*+*]:border-white/[0.08]">
              {searchResults.map((place) => (
                <button
                  type="button"
                  key={place.id}
                  onClick={() => void selectPlace(place)}
                  className="flex w-full flex-col gap-0.5 px-3 py-2.5 text-left transition active:bg-black/[0.03] dark:active:bg-white/[0.04]"
                >
                  <span className="truncate text-[15px] font-medium text-darkText">
                    {place.name}
                  </span>
                  <span className="truncate text-[12px] text-lightText">
                    {place.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {searching && (
          <div className="flex items-center gap-2 px-1 text-[12px] text-lightText">
            <ClipLoader size={12} color="currentColor" />
            <TextTranslate text="Searching" />
          </div>
        )}
      </div>

      <div className="relative mt-3 shrink-0 px-4">
        <div className="overflow-hidden rounded-2xl shadow-sm ring-1 ring-black/[0.04] dark:ring-white/[0.06]">
          {loadingGeo && !hasCoords ? (
            <div className="grid h-64 place-content-center bg-secondary">
              <ClipLoader size={22} color="#007AFF" />
            </div>
          ) : (
            <InteractiveLocationMap
              lat={hasCoords ? lat : 55.751244}
              lng={hasCoords ? lng : 37.618423}
              provider={provider}
              onChange={(nextLat, nextLng) => {
                void handleMapChange(nextLat, nextLng);
              }}
              className="h-64 w-full"
            />
          )}
        </div>

        <button
          type="button"
          onClick={locateUser}
          className="absolute bottom-3 right-7 grid h-10 w-10 place-content-center rounded-full bg-primary text-themeColor shadow-md ring-1 ring-black/5 transition active:scale-95 dark:ring-white/10"
          aria-label="My location"
        >
          <MdMyLocation className="text-xl" />
        </button>
      </div>

      {geoError ? (
        <div className="px-4 pt-2 text-[12px] text-[#FF3B30]">{geoError}</div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-28 pt-4">
        <div className="mb-1.5 px-1 text-[13px] font-medium uppercase tracking-wide text-lightText">
          <TextTranslate text="Nearby Places" />
        </div>
        <div className="overflow-hidden rounded-2xl bg-secondary shadow-sm ring-1 ring-black/[0.04] dark:ring-white/[0.06]">
          {loadingNearby ? (
            <div className="grid h-28 place-content-center">
              <ClipLoader size={18} color="#007AFF" />
            </div>
          ) : nearbyPlaces.length === 0 ? (
            <div className="px-4 py-8 text-center text-[14px] text-lightText">
              <TextTranslate text="No nearby places found" />
            </div>
          ) : (
            <div className="[&>*+*]:border-t [&>*+*]:border-black/[0.06] dark:[&>*+*]:border-white/[0.08]">
              {nearbyPlaces.map((place) => (
                <button
                  type="button"
                  key={place.id}
                  onClick={() => void selectPlace(place)}
                  className="flex w-full items-start gap-3 px-3 py-3 text-left transition active:bg-black/[0.03] dark:active:bg-white/[0.04]"
                >
                  <div className="mt-0.5 grid h-8 w-8 shrink-0 place-content-center rounded-full bg-themeColor/15 text-themeColor">
                    <MdMyLocation className="text-base" />
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-[15px] font-semibold text-darkText">
                      {place.name}
                    </div>
                    <div className="mt-0.5 line-clamp-2 text-[12px] text-lightText">
                      {place.label}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <ShareLocation onClose={onClose} />
    </div>
  );
}
